import { Stagger, StaggerItem, Counter } from "@/components/motion";

const STATS = [
  {
    value: 150,
    suffix: "+",
    label: "Projects Delivered",
    sub: "Across 14 countries",
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    color: "#09B2ED",
  },
  {
    value: 12,
    suffix: "k+",
    label: "Hours Automated",
    sub: "Saved for our clients",
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    color: "#2563EB",
  },
  {
    value: 98,
    suffix: "%",
    label: "Client Retention",
    sub: "Long-term partnerships",
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    color: "#09B2ED",
  },
  {
    value: 14,
    suffix: "+",
    label: "Countries Served",
    sub: "Global delivery team",
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: "#2563EB",
  },
];

export default function StatsBand() {
  return (
    <section
      className="relative px-5 sm:px-8 py-20 lg:py-24 overflow-hidden bg-[#01193D]"
      data-testid="stats-band-section"
    >
      {/* Background glow orbs */}
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #09B2ED, transparent 70%)" }} />
      <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #2563EB, transparent 70%)" }} />

      {/* Dot-grid overlay */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #CAF2F9 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Section eyebrow */}
        <p className="text-center text-[10px] font-mono uppercase tracking-[0.2em] text-[#09B2ED] mb-10">
          By the numbers
        </p>

        <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 rounded-3xl overflow-hidden" stagger={0.12}>
          {STATS.map((s, i) => (
            <StaggerItem key={s.label}>
              <div className="relative flex flex-col items-center text-center px-6 py-10 bg-[#01193D] h-full group hover:bg-white/[0.04] transition-colors duration-300">

                {/* Icon bubble */}
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:-translate-y-1"
                  style={{ background: `${s.color}18`, color: s.color, border: `1px solid ${s.color}30` }}
                >
                  {s.icon}
                </div>

                {/* Counter number */}
                <p
                  className="font-display font-bold text-4xl lg:text-5xl leading-none"
                  style={{ color: s.color }}
                  data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Counter value={s.value} suffix={s.suffix} />
                </p>

                {/* Label */}
                <p className="text-sm font-semibold text-white mt-3">{s.label}</p>

                {/* Supporting sub-text */}
                <p className="text-[10px] text-[#91E0F1]/60 font-mono mt-1">{s.sub}</p>

                {/* Bottom glow accent line */}
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] w-0 group-hover:w-[60%] transition-all duration-500 rounded-full"
                  style={{ background: `linear-gradient(90deg, transparent, ${s.color}, transparent)` }}
                />
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Bottom tagline */}
        <p className="text-center text-sm text-[#91E0F1]/50 font-mono mt-10 tracking-wide">
          Built by a team that ships — then keeps shipping.
        </p>
      </div>
    </section>
  );
}
